import type { AuthError } from '@supabase/supabase-js';
import type { FriendlyError, SupabaseAuthError, ErrorDefinition } from './types';
import { AUTH_ERRORS } from './authErrors';
import { NETWORK_ERRORS } from './networkErrors';

const ERROR_MAP: Record<string, ErrorDefinition> = { ...NETWORK_ERRORS, ...AUTH_ERRORS };

const FALLBACK_ERROR: ErrorDefinition = {
  title: 'Oops, something went wrong',
  message: "We encountered an unexpected issue. Don't worry, your data is safe.",
  action: 'Please try again. If this keeps happening, contact support.',
  retryable: true,
  category: 'unknown',
  recoverySuggestions: ['Try again in a few moments', 'Restart the app if the issue persists'],
};

const MESSAGE_PATTERNS: Array<{ pattern: RegExp; code: string }> = [
  { pattern: /rate limit|too many/i, code: 'over_request_rate_limit' },
  { pattern: /expired|no longer valid/i, code: 'otp_expired' },
  { pattern: /invalid.*code|incorrect.*code|wrong.*code|invalid.*otp/i, code: 'invalid_credentials' },
  { pattern: /email.*invalid|invalid.*email/i, code: 'email_address_invalid' },
  { pattern: /not found|no user/i, code: 'user_not_found' },
  { pattern: /banned|suspended/i, code: 'user_banned' },
  { pattern: /network|connection|offline/i, code: 'network_error' },
  { pattern: /timeout|timed out/i, code: 'network_timeout' },
  { pattern: /captcha/i, code: 'captcha_failed' },
  { pattern: /already.*exist|email.*exist/i, code: 'email_exists' },
];

function extractErrorCode(error: SupabaseAuthError | AuthError | Error): string {
  if ('code' in error && typeof error.code === 'string' && error.code) return error.code;
  const message = error.message || '';
  for (const { pattern, code } of MESSAGE_PATTERNS) {
    if (pattern.test(message)) return code;
  }
  if (error.name === 'TypeError' && message.toLowerCase().includes('network')) return 'network_error';
  if ('status' in error) {
    const status = (error as SupabaseAuthError).status;
    if (status === 429) return 'over_request_rate_limit';
    if (status === 503) return 'server_unavailable';
    if (status === 504) return 'request_timeout';
  }
  return 'unknown';
}

function isOffline(): boolean {
  return !navigator.onLine;
}

function extractRetryAfterSeconds(message: string | undefined): number | undefined {
  if (!message) return undefined;
  const match = message.match(/after\s+(\d+)\s+seconds?/i);
  return match ? parseInt(match[1], 10) : undefined;
}

export async function parseSupabaseError(
  error: SupabaseAuthError | AuthError | Error | null
): Promise<FriendlyError> {
  if (!error) return { ...FALLBACK_ERROR, code: 'no_error' };
  if (isOffline()) {
    const networkError = ERROR_MAP['network_offline'];
    return { ...networkError, code: 'network_offline' };
  }
  const code = extractErrorCode(error);
  const definition = ERROR_MAP[code] || FALLBACK_ERROR;
  const dynamicRetry = extractRetryAfterSeconds(error.message);
  return { title: definition.title, message: definition.message, action: definition.action, retryable: definition.retryable, retryAfterSeconds: dynamicRetry ?? definition.retryAfterSeconds, recoverySuggestions: definition.recoverySuggestions, category: definition.category, code };
}

export function parseSupabaseErrorSync(
  error: SupabaseAuthError | AuthError | Error | null
): FriendlyError {
  if (!error) return { ...FALLBACK_ERROR, code: 'no_error' };
  const code = extractErrorCode(error);
  const definition = ERROR_MAP[code] || FALLBACK_ERROR;
  const dynamicRetry = extractRetryAfterSeconds(error.message);
  return { title: definition.title, message: definition.message, action: definition.action, retryable: definition.retryable, retryAfterSeconds: dynamicRetry ?? definition.retryAfterSeconds, recoverySuggestions: definition.recoverySuggestions, category: definition.category, code };
}

export { ERROR_MAP, FALLBACK_ERROR };
